"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.toTargetColumnId = toTargetColumnId;
exports.toTargetValues = toTargetValues;
exports.toAppColumnId = toAppColumnId;
exports.toAppRow = toAppRow;
exports.toAppColumns = toAppColumns;
exports.toAppSourceQueryReader = toAppSourceQueryReader;
const database_identity_1 = require("@streamlain/database-identity");
const app_base_contract_1 = require("./app-base-contract");
const app_base_source_query_1 = require("./app-base-source-query");
function toTargetColumnId(binding, appColumnId) {
    return binding.columnMap?.[appColumnId] ?? appColumnId;
}
/** Re-keys App-space cell values to the bound table's column ids. */
function toTargetValues(binding, values) {
    return Object.fromEntries(Object.entries(values).map(([appColumnId, value]) => [
        toTargetColumnId(binding, appColumnId),
        value,
    ]));
}
/**
 * App column id of one bound column, or null when the target id equals an App
 * id the map routes elsewhere (the App must not see that column under it).
 */
function toAppColumnId(binding, targetColumnId) {
    const entries = Object.entries(binding.columnMap ?? {});
    const mapped = entries.find(([, target]) => target === targetColumnId);
    if (mapped)
        return mapped[0];
    return entries.some(([appColumnId]) => appColumnId === targetColumnId)
        ? null
        : targetColumnId;
}
function hasColumnMap(binding) {
    return Object.entries(binding.columnMap ?? {}).some(([appColumnId, targetColumnId]) => appColumnId !== targetColumnId);
}
/** Re-keys one bound-table row to App column ids. Pseudo cells pass through. */
function toAppRow(binding, row) {
    if (!hasColumnMap(binding))
        return row;
    const cells = {};
    for (const [targetColumnId, cell] of Object.entries(row.cells)) {
        const appColumnId = toAppColumnId(binding, targetColumnId);
        if (appColumnId !== null)
            cells[appColumnId] = cell;
    }
    return { ...row, cells };
}
/** Bound-table column descriptors under their App column ids. */
function toAppColumns(binding, columns) {
    return columns.flatMap(column => {
        const id = toAppColumnId(binding, column.id);
        return id === null ? [] : [{ ...column, id }];
    });
}
/**
 * Presents an admitted target-space reader in App column ids, so query
 * parsing, slug predicates, filters, sorts and returned rows all speak the
 * App's ids. Scope, visibility and localization stay in target space below.
 */
function toAppSourceQueryReader(binding, reader) {
    if (!hasColumnMap(binding))
        return reader;
    return {
        ...reader,
        accessScope: (0, app_base_contract_1.appBaseSha256Hex)((0, app_base_source_query_1.appBaseQueryIdentity)([reader.accessScope, binding.columnMap])),
        index: {
            ...reader.index,
            columns: toAppColumns(binding, reader.index.columns),
        },
        readChunk: async (shardId) => {
            const chunk = await reader.readChunk(shardId);
            return { ...chunk, rows: chunk.rows.map(row => toAppRow(binding, row)) };
        },
        ...(reader.shardValueSummary
            ? {
                shardValueSummary: (shardId) => (0, database_identity_1.remapDatabaseShardValueColumns)(reader.shardValueSummary?.(shardId), id => toAppColumnId(binding, id)),
            }
            : {}),
    };
}
